import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../api/types/settings/settings-all';
import { CoursePollWithQuestionsResponse } from '../../../../api/types/students/user-details-student';
import { FeedbackPollCard } from './components/feedback-poll-card';
import { AnswerPollDialog } from './dialogs/answer-poll-dialog';
import { EmptyPollsState } from './components/empty-polls-state';

export const MyCourseFeedbacksPolls = observer(() => {
  const [selectedPoll, setSelectedPoll] =
    useState<CoursePollWithQuestionsResponse | null>(null);

  // Get student profile - all hooks must be called before any early returns
  const studentProfile = globalUserProfile.userProfile?.profile as
    | StudentProfile
    | undefined;

  // Find programId for a given courseId by searching through programWithCourses
  const findProgramIdByCourseId = (
    courseId: number,
    profile: StudentProfile | undefined,
  ): number | undefined => {
    if (!profile) return undefined;
    const programWithCourses = profile.programWithCourses || [];
    for (const program of programWithCourses) {
      const course = program.courses?.find((c) => c.id === courseId);
      if (course) {
        return program.id;
      }
    }
    return undefined;
  };

  // Get programId for selected poll - must be called before early return
  const selectedPollProgramId = useMemo(() => {
    if (!selectedPoll || !studentProfile) return undefined;
    // First check if programId is already in the poll data
    if (selectedPoll.programId) return selectedPoll.programId;
    // Otherwise, find it from programWithCourses
    return findProgramIdByCourseId(selectedPoll.courseId, studentProfile);
  }, [selectedPoll, studentProfile]);

  // Check if user profile exists and is a student - AFTER all hooks
  if (!globalUserProfile.userProfile?.profile || !studentProfile) {
    return <div>טוען נתונים...</div>;
  }

  const polls: CoursePollWithQuestionsResponse[] =
    studentProfile.coursePollFeedbacks || [];

  return (
    <div style={{ paddingTop: '16px', direction: 'rtl' }}>
      {/* Confirm Dialog for submission */}
      <ConfirmDialog />

      {/* Page Title */}
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '8px',
        }}
      >
        משובים למרצים
      </div>
      <p
        style={{
          color: '#6b7280',
          fontSize: '14px',
          marginBottom: '24px',
        }}
      >
        כאן תוכל למלא משובים על הקורסים והמרצים. המשוב שלך חשוב לנו ומסייע
        בשיפור איכות ההוראה.
      </p>

      {/* Answer Poll Dialog */}
      <Dialog
        header={
          selectedPoll
            ? `משוב: ${selectedPoll.pollName} - ${selectedPoll.courseName}`
            : 'משוב'
        }
        visible={selectedPoll !== null}
        onHide={() => setSelectedPoll(null)}
        style={{
          width: '650px',
          maxWidth: '95vw',
          backgroundColor: 'white',
          borderRadius: '12px',
        }}
        maximizable
        blockScroll
      >
        {selectedPoll && selectedPollProgramId && (
          <AnswerPollDialog
            poll={selectedPoll}
            programId={selectedPollProgramId}
            closeDialog={() => setSelectedPoll(null)}
          />
        )}
      </Dialog>

      {/* Polls List */}
      {polls.length === 0 ? (
        <EmptyPollsState />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {polls.map((poll) => (
            <FeedbackPollCard
              key={`${poll.courseId}-${poll.id}`}
              poll={poll}
              onClick={() => setSelectedPoll(poll)}
            />
          ))}
        </div>
      )}
    </div>
  );
});
